import { ScoreCriteriaType } from "@/pages/Private/pages/Score/schema/score.schema";

import { ListCriterionRef, readTierValues, Tier, writeTierValues } from "./criteriaTiers";

/**
 * Moving one value between a criterion's points buckets by dragging its chip.
 *
 * Re-grading a country meant removing the chip from one row and typing it into
 * another, which on the location criteria means finding it in the taxonomy again
 * (PREC-3719). Plain HTML5 drag-and-drop rather than dnd-kit: a chip only ever
 * moves between four fixed drop targets in the same card — there is no sorting,
 * no overlay and no cross-list state to coordinate — and it keeps the drop
 * behaviour reachable from a test instead of a synthetic pointer sequence.
 */

/**
 * The drag payload's media type. A private type rather than `text/plain` so a
 * chip box never accepts arbitrary dragged text — `dragover` may inspect the
 * types but not the data, so the type is all there is to decide on.
 */
export const TIER_CHIP_MEDIA_TYPE = "application/x-signalist-tier-chip";

export interface TierChipDrag {
	/** Rubric key of the criterion the chip belongs to (`HQ_COUNTRY`, …). */
	criterion: string;
	/** The bucket it was dragged out of. */
	tier: Tier;
	value: string;
}

export const encodeTierChipDrag = (payload: TierChipDrag): string => JSON.stringify(payload);

/** Reads a drag payload back, or null for anything that is not one of ours. */
export const decodeTierChipDrag = (raw: string): TierChipDrag | null => {
	if (!raw) {
		return null;
	}

	try {
		const parsed: unknown = JSON.parse(raw);

		if (typeof parsed !== "object" || parsed === null) {
			return null;
		}

		const { criterion, tier, value } = parsed as Record<string, unknown>;

		if (typeof criterion !== "string" || typeof tier !== "number" || typeof value !== "string") {
			return null;
		}

		return { criterion, tier: tier as Tier, value };
	} catch {
		return null;
	}
};

/**
 * Moves one value from one bucket of a criterion to another, in a single write.
 *
 * Returns the criteria unchanged (same reference) when the move would not change
 * anything — dropped on its own bucket, or on one that already lists it — so the
 * caller can skip marking the draft dirty.
 */
export const moveTierValue = (
	criteria: ScoreCriteriaType,
	ref: ListCriterionRef,
	from: Tier,
	to: Tier,
	value: string
): ScoreCriteriaType => {
	if (from === to) {
		return criteria;
	}

	const source = readTierValues(criteria, ref, from);

	if (!source.includes(value)) {
		return criteria;
	}

	const target = readTierValues(criteria, ref, to);

	if (target.includes(value)) {
		return criteria;
	}

	// Emptied first: writing the target bucket takes the "Rest of the world"
	// catch-all out of every other bucket, and it must not find the one it came
	// from still holding it (see `writeTierValues`).
	const emptied = writeTierValues(
		criteria,
		ref,
		from,
		source.filter((entry) => entry !== value)
	);

	return writeTierValues(emptied, ref, to, [...target, value]);
};
